import {
	Box,
	Button,
	Flex,
	Text,
	Table,
	Tbody,
	Tr,
	Td,
	Input,
	InputGroup,
	InputLeftElement,
	Alert,
	AlertIcon,
	IconButton,
} from 'src/components/ui';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { generateBudgetBreakdownByPlacements } from 'src/components/campaign/CampaignBudget/parts/utils';
import { CampaignContext } from 'src/contexts';
import { CHANNELS_NAME_MAPPING } from 'src/lib/constants';
import { IChannelGroup } from 'src/lib/schemas';
import { ICampaignBudget } from 'src/lib/schemas/campaign/newFlowCampaign';
import { createOrUpdateCampaign } from 'src/services/campaign';
import { customToast } from 'src/services/toast';

interface IBudgetBreakdownProps {
	budget?: ICampaignBudget;
	availableChannelGroups: IChannelGroup[] | null;
	setIsValidCampaignBudget: (val: boolean) => void;
	onBudgetChange?: (newBudget: ICampaignBudget) => void;
}

const formatAmount = (amount: number) => {
	return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
};

const BudgetBreakdown = ({
	budget,
	availableChannelGroups,
	setIsValidCampaignBudget,
	onBudgetChange,
}: IBudgetBreakdownProps) => {
	const { id: campaignId, campaign, setCampaign } = useContext(CampaignContext);
	const [isEditing, setIsEditing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [editedChannels, setEditedChannels] = useState(
		() =>
			budget?.breakdown?.map((item) => ({
				id: item.id,
				name: CHANNELS_NAME_MAPPING[item.id],
				amount: item.budget,
			})) || [],
	);
	const [editedBreakdown, setEditedBreakdown] = useState(
		() => budget?.breakdown || [],
	);

	const [allocationError, setAllocationError] = useState<string | null>(null);

	const remainingBudget = useMemo(() => {
		const totalAssigned = editedChannels.reduce(
			(total, channel) => total + channel.amount,
			0,
		);
		return (budget?.total ?? 0) - totalAssigned;
	}, [editedChannels, budget?.total]);

	useEffect(() => {
		if (!isEditing) {
			setIsValidCampaignBudget(!allocationError);
		}
	}, [allocationError, setIsValidCampaignBudget, isEditing]);

	useEffect(() => {
		if (!isEditing && !isSaving) {
			const tolerance = 0.01;
			if (remainingBudget > tolerance) {
				setAllocationError(
					`Pending to allocate $${formatAmount(remainingBudget)}`,
				);
			} else if (remainingBudget < -tolerance) {
				setAllocationError(
					`Over budget by $${formatAmount(Math.abs(remainingBudget))}`,
				);
			} else {
				setAllocationError(null);
			}
		}
	}, [remainingBudget, isEditing, isSaving]);

	const generatedAIAllocation = useMemo(() => {
		const placements = campaign?.placements ?? [];
		const total = budget?.total ?? 0;

		// Don't regenerate if no placements or budget
		if (placements.length === 0 || total === 0) {
			return [];
		}

		return generateBudgetBreakdownByPlacements(
			placements,
			availableChannelGroups ?? [],
			total,
		);
	}, [campaign?.placements, availableChannelGroups, budget?.total]);

	const isBudgetItemVisible = (item: any) => {
		return !availableChannelGroups?.some((group) =>
			group.channels.some(
				(channel) => channel.hidden_budget && channel.id === item.id,
			),
		);
	};

	// Reset editing mode when placements change to show updated AI allocation
	useEffect(() => {
		setIsEditing(false);
	}, [campaign?.placements]);

	// Single source of truth for editedChannels
	const prevGeneratedAllocationRef = useRef<any[]>([]);
	const prevBudgetBreakdownRef = useRef<any[]>([]);

	useEffect(() => {
		if (isEditing) {
			// In editing mode, only update from saved budget breakdown if it actually changed
			const hasBudgetBreakdownChanged =
				JSON.stringify(prevBudgetBreakdownRef.current) !==
				JSON.stringify(budget?.breakdown);

			if (budget?.breakdown?.length && hasBudgetBreakdownChanged) {
				setEditedChannels(
					budget.breakdown?.map((item) => ({
						id: item.id,
						name: CHANNELS_NAME_MAPPING[item.id],
						amount: item.budget,
						hidden_budget: item.hidden_budget,
					})) || [],
				);
				setEditedBreakdown(budget.breakdown || []);
				prevBudgetBreakdownRef.current = budget.breakdown;
			}
		} else {
			// Not in editing mode: check if channels changed (new placements added/removed)
			const currentChannelIds = editedChannels.map((ch) => ch.id).sort().join(',');
			const aiChannelIds = generatedAIAllocation.map((ch) => ch.id).sort().join(',');
			const channelsChanged = currentChannelIds !== aiChannelIds;

			if (channelsChanged && generatedAIAllocation.length > 0) {
				// Channels were added/removed — use AI allocation to reflect new channels
				setEditedChannels(
					generatedAIAllocation.map((item) => ({
						id: item.id,
						name: CHANNELS_NAME_MAPPING[item.id],
						amount: item.budget,
						hidden_budget: false,
					})),
				);
				setEditedBreakdown(generatedAIAllocation);
				prevGeneratedAllocationRef.current = generatedAIAllocation;
				prevBudgetBreakdownRef.current = [];
			} else if (budget?.breakdown?.length) {
				// Same channels — use saved breakdown if it changed
				const hasBudgetBreakdownChanged =
					JSON.stringify(prevBudgetBreakdownRef.current) !==
					JSON.stringify(budget?.breakdown);

				if (hasBudgetBreakdownChanged) {
					setEditedChannels(
						budget.breakdown.map((item) => ({
							id: item.id,
							name: CHANNELS_NAME_MAPPING[item.id],
							amount: item.budget,
							hidden_budget: item.hidden_budget,
						})),
					);
					setEditedBreakdown(budget.breakdown);
					prevBudgetBreakdownRef.current = budget.breakdown;
				}
			} else if (!budget?.breakdown?.length) {
				// No saved breakdown — fall back to AI allocation
				const hasGeneratedAllocationChanged =
					JSON.stringify(prevGeneratedAllocationRef.current) !==
					JSON.stringify(generatedAIAllocation);

				if (hasGeneratedAllocationChanged && generatedAIAllocation.length > 0) {
					setEditedChannels(
						generatedAIAllocation.map((item) => ({
							id: item.id,
							name: CHANNELS_NAME_MAPPING[item.id],
							amount: item.budget,
							hidden_budget: false,
						})),
					);
					setEditedBreakdown(generatedAIAllocation);
					prevGeneratedAllocationRef.current = generatedAIAllocation;
				}
			}
		}
	}, [isEditing, generatedAIAllocation, budget?.breakdown]);

	const handleManualDefineClick = () => {
		setIsEditing(true);
		setAllocationError(null);
	};

	const handleConfirm = async () => {
		// Validate: no negative values
		const hasNegative = editedChannels.some((ch) => ch.amount < 0);
		if (hasNegative) {
			customToast('Budget values cannot be negative.', 'error');
			return;
		}

		// Validate: all values must be integers
		const hasDecimal = editedChannels.some((ch) => !Number.isInteger(ch.amount));
		if (hasDecimal) {
			customToast('Budget values must be whole numbers.', 'error');
			return;
		}

		const resolvedCampaignId = campaignId !== 'new' ? campaignId : campaign?.id;
		if (!resolvedCampaignId || resolvedCampaignId === 'new' || !budget) return;

		const newTotal = editedChannels.reduce((sum, ch) => sum + ch.amount, 0);
		const newBreakdown = editedBreakdown.map((item) => {
			const edited = editedChannels.find((ch) => ch.id === item.id);
			return edited ? { ...item, budget: edited.amount } : item;
		});

		setIsSaving(true);
		try {
			await createOrUpdateCampaign(
				{
					budget: {
						...budget,
						total: newTotal,
						breakdown: newBreakdown,
					},
				},
				resolvedCampaignId,
			);

			// Update local campaign context
			if (campaign) {
				setCampaign({
					...campaign,
					budget: {
						...budget,
						total: newTotal,
						breakdown: newBreakdown,
					},
				});
			}

			// Notify parent of full budget change (total + breakdown)
			if (onBudgetChange) {
				onBudgetChange({
					...budget,
					total: newTotal,
					breakdown: newBreakdown,
				});
			}

			setAllocationError(null);
			setIsEditing(false);
		} catch (error) {
			console.error('Error saving budget breakdown:', error);
			customToast('Error saving budget. Please try again.', 'error');
		}
		setIsSaving(false);
	};

	const handleCancel = () => {
		// Revert to AI allocation
		setEditedChannels(
			generatedAIAllocation.map((item) => ({
				id: item.id,
				name: CHANNELS_NAME_MAPPING[item.id],
				amount: item.budget,
			})),
		);
		setEditedBreakdown(generatedAIAllocation);
		setIsEditing(false);
		setAllocationError(null);
	};

	const handleAmountChange = useCallback(
		(id: string, value: string) => {
			const parsedValue = value === '' ? 0 : Math.floor(Math.abs(parseInt(value, 10) || 0));

			setEditedChannels((prevChannels) =>
				prevChannels.map((channel) =>
					channel.id === id ? { ...channel, amount: parsedValue } : channel,
				),
			);

			setEditedBreakdown((prevBreakdown) =>
				prevBreakdown.map((item) =>
					item.id === id ? { ...item, budget: parsedValue } : item,
				),
			);
		},
		[],
	);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		const invalidChars = ['e', 'E', '+', '-', '.'];

		if (invalidChars.includes(e.key)) {
			e.preventDefault();
		}
	};

	const getEditingTotal = () => {
		return editedChannels.reduce((total, ch) => total + ch.amount, 0);
	};

	const getPercentage = (itemBudget: number) => {
		const total = isEditing ? getEditingTotal() : (budget?.total ?? 0);
		return total ? (itemBudget * 100) / total : 0;
	};

	const channels = editedChannels.map((item) => ({
		...item,
		percentage: getPercentage(item.amount),
	}));

	const getTotalAmount = () => {
		return channels.reduce((total, channel) => {
			return total + (channel.amount || 0);
		}, 0);
	};

	const getTotalPercentage = () => {
		if (isEditing) return 100;
		const totalPercentage = channels.reduce((total, channel) => {
			return total + getPercentage(channel.amount);
		}, 0);
		return Math.min(totalPercentage, 100);
	};

	// Don't render if no paid channels are selected
	if (channels.length === 0) {
		return null;
	}

	return (
		<Box borderWidth="1px" borderRadius="md">
			<Flex
				direction={{ base: 'column', md: 'row' }}
				justifyContent="space-between"
				alignItems={{ base: 'stretch', md: 'center' }}
				w="full"
				p={{ base: 3, md: 4 }}
				borderBottomWidth="1px"
				gap={{ base: 3, md: 0 }}
			>
				<Text size="md" fontWeight={500}>Budget breakdown</Text>
				{isEditing ? (
					<Flex gap={2} justifyContent={{ base: 'stretch', md: 'flex-end' }}>
						<Button
							variant="orangeOutline"
							onClick={handleCancel}
							size={{ base: 'sm', md: 'md' }}
							flex={{ base: 1, md: 'none' }}
							isDisabled={isSaving}
						>
							Revert to AI allocation
						</Button>
						<IconButton
							aria-label="Confirm budget"
							icon={<CheckIcon />}
							colorScheme="green"
							variant="solid"
							onClick={handleConfirm}
							isLoading={isSaving}
							size={{ base: 'sm', md: 'md' }}
						/>
						<IconButton
							aria-label="Cancel editing"
							icon={<CloseIcon />}
							colorScheme="red"
							variant="outline"
							onClick={handleCancel}
							isDisabled={isSaving}
							size={{ base: 'sm', md: 'md' }}
						/>
					</Flex>
				) : (
					<Button
						variant="orangeOutline"
						onClick={handleManualDefineClick}
						size={{ base: 'sm', md: 'md' }}
						w={{ base: 'full', md: 'auto' }}
					>
						Define manually
					</Button>
				)}
			</Flex>
			<Box p={{ base: 3, md: 4 }} bg="gray.50" borderRadius="md">
				{allocationError && (
					<Alert status="error" mb={4}>
						<AlertIcon />
						{allocationError}
					</Alert>
				)}

				{/* Mobile: Card-based layout */}
				<Box display={{ base: 'block', md: 'none' }}>
					<Flex direction="column" gap={2}>
						{channels
							.filter((channel) => isBudgetItemVisible(channel))
							.map((channel) => (
								<Box key={channel.id} bg="white" p={3} borderRadius="md" borderWidth="1px">
									<Flex justifyContent="space-between" alignItems="center" mb={2}>
										<Text fontSize="sm" fontWeight="medium">
											{channel.name} Ads
										</Text>
										<Text fontSize="sm" color="gray.600">
											{formatAmount(channel.percentage)}%
										</Text>
									</Flex>
									<Flex alignItems="center" gap={2}>
										{isEditing ? (
											<InputGroup flex={1}>
												<InputLeftElement
													pointerEvents="none"
													color="gray.300"
													fontSize="sm"
													children="$"
												/>
												<Input
													bg="white"
													borderRadius="md"
													type="number"
													min={0}
													step={1}
													value={
														channel.amount !== 0
															? String(channel.amount)
															: 0
													}
													onChange={(e) =>
														handleAmountChange(channel.id, e.target.value)
													}
													onKeyDown={handleKeyDown}
													size="sm"
													fontSize="sm"
													pl={8}
												/>
											</InputGroup>
										) : (
											<Text fontSize="lg" fontWeight="bold" color="#F7480B">
												${formatAmount(channel.amount)}
											</Text>
										)}
									</Flex>
								</Box>
							))}

						{/* Total row for mobile */}
						<Box bg="gray.100" p={3} borderRadius="md" borderWidth="1px">
							<Flex justifyContent="space-between" alignItems="center" mb={2}>
								<Text fontSize="sm" fontWeight="bold">Total</Text>
								<Text fontSize="sm" fontWeight="bold" color="gray.600">
									{formatAmount(getTotalPercentage())}%
								</Text>
							</Flex>
							<Text fontSize="lg" fontWeight="bold">
								${formatAmount(getTotalAmount())}
							</Text>
						</Box>
					</Flex>
				</Box>

				{/* Desktop: Table layout */}
				<Box borderRadius="md" overflow="auto" borderWidth="1px" display={{ base: 'none', md: 'block' }}>
					<Table variant="simple" size="md">
						<Tbody bg="white">
							{channels
								.filter((channel) => isBudgetItemVisible(channel))
								.map((channel) => (
									<Tr key={channel.id}>
										<Td fontSize="sm" py={3}>
											{channel.name} Ads
										</Td>
										<Td isNumeric py={3}>
											{isEditing ? (
												<InputGroup justifyContent="flex-end" maxW="100px" float="right">
													<Input
														bg="white"
														borderRadius="md"
														type="number"
														min={0}
														step={1}
														value={
															channel.amount !== 0
																? String(channel.amount)
																: 0
														}
														textAlign="right"
														onChange={(e) =>
															handleAmountChange(channel.id, e.target.value)
														}
														onKeyDown={handleKeyDown}
														size="sm"
														fontSize="sm"
													/>
													<InputLeftElement
														alignSelf="center"
														pointerEvents="none"
														color="gray.300"
														fontSize="1em"
														children="$"
														top="50%"
														bottom="50%"
													/>
												</InputGroup>
											) : (
												<Text fontSize="sm">
													${formatAmount(channel.amount)}
												</Text>
											)}
										</Td>
										<Td isNumeric fontSize="sm" py={3}>
											{formatAmount(channel.percentage)}%
										</Td>
									</Tr>
								))}
						</Tbody>
						<Tbody bg="gray.100">
							<Tr>
								<Td fontSize="sm" fontWeight="bold" py={3}>
									Total
								</Td>
								<Td isNumeric fontSize="sm" fontWeight="bold" py={3}>
									${formatAmount(getTotalAmount())}
								</Td>
								<Td isNumeric fontSize="sm" fontWeight="bold" py={3}>
									{formatAmount(getTotalPercentage())}%
								</Td>
							</Tr>
						</Tbody>
					</Table>
				</Box>
			</Box>
		</Box>
	);
};

export default BudgetBreakdown;
